import React, { useState } from "react";
import {
  Attachment,
  MessageTimestamp,
  useChannelStateContext,
  useChatContext,
  useMessageContext,
} from "stream-chat-react";
import type { LocalMessage } from "stream-chat";
import {
  useTranscribeMessage,
  useTranslateMessage,
} from "@/hooks/useChat.hooks";
import { Button } from "@/components/ui/button";
import {
  AudioLines,
  CheckCheck,
  Languages,
  MessageSquareQuote,
  Pin,
  PinOff,
  RefreshCcw,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useTranslation } from "react-i18next";
import { RequestMessageDisplay } from "./RequestMessageDisplay";
import { ExternalChannelBadge } from "./ExternalChannelBadge";
import {
  MessageDeliveryFailure,
  isFailedDeliveryStatus,
} from "./MessageDeliveryFailure";
import { ResolvedChannelValue } from "@/types/chat-external-message";
import { MessageDeliveryStatus } from "@/types/message-delivery";
import { useHotel } from "@/hooks/useHotel";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { WhatsAppMediaAttachment } from "./WhatsAppMediaAttachment";
import { isAllMentionMessage } from "@/components/pages/chats/utils/channel-mentions";
import { useTranslationPreferences } from "@/components/pages/chats/hooks/useTranslationPreferences";
import { dispatchChatComposerInsertText } from "@/components/pages/chats/utils/composer-events";

interface UserAvatarProps {
  isMyMessage: boolean;
  isOnline: boolean;
  message: LocalMessage;
  onlineLabel: string;
}

interface SuggestedCannedResponse {
  id: string;
  text: string;
  categoryId?: string;
  matchScore?: number;
  status?: string;
}

const getBubbleBackground = (isOwnMessage: boolean, highlighted: boolean) => {
  if (highlighted) {
    return isOwnMessage
      ? "border border-neutral-300 bg-neutral-200"
      : "border border-stone-300 bg-royal-glow-4";
  }

  return isOwnMessage
    ? "border border-neutral-200 bg-white"
    : "border border-stone-200 bg-royal-glow-5";
};

const UserAvatar = ({
  isMyMessage,
  isOnline,
  message,
  onlineLabel,
}: UserAvatarProps) => {
  const getInitials = (name?: string) => {
    if (!name) return "U";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const avatarFallbackStyles = isMyMessage
    ? "bg-skyline-4 text-skyline-1"
    : "bg-eathy-comfort-4/50 text-eathy-comfort-1";

  return (
    <div className="relative shrink-0">
      <Avatar className="size-8 rounded-lg border-none">
        <AvatarImage
          src={message.user?.image}
          alt={message.user?.name || "User"}
          className="rounded-lg"
        />
        <AvatarFallback
          className={`rounded-lg text-xs font-medium ${avatarFallbackStyles}`}
        >
          {getInitials(message.user?.name || message.user?.id)}
        </AvatarFallback>
      </Avatar>
      {isOnline && (
        <span
          className="border-background bg-accent-green absolute right-0 bottom-0 size-2.5 rounded-full border-2"
          role="img"
          aria-label={onlineLabel}
          title={onlineLabel}
        />
      )}
    </div>
  );
};

const TranscriptionSection = ({
  isTranscribed,
  t,
  handleTranslateTranscriptionClick,
  isTranslating,
  showTranscriptionTranslation,
  isTranscriptionTranslated,
  translatedTranscription,
  transcription,
}: {
  isTranscribed: boolean;
  t: any;
  handleTranslateTranscriptionClick: () => void;
  isTranslating: boolean;
  showTranscriptionTranslation: boolean;
  isTranscriptionTranslated: boolean;
  translatedTranscription?: string;
  transcription?: string;
}) => {
  if (!isTranscribed) return null;

  return (
    <div className="border-t border-black/5 pt-2 text-sm break-words text-slate-600 italic opacity-70">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-1 text-[9px] font-bold uppercase not-italic">
          <AudioLines className="h-3 w-3" /> {t("chats.transcription")}
        </div>
        <Button
          variant="ghost"
          className="h-4 px-1 text-[8px] hover:bg-black/5"
          onClick={handleTranslateTranscriptionClick}
          disabled={isTranslating}
        >
          {isTranslating ? (
            <RefreshCcw className="mr-1 h-2 w-2 animate-spin" />
          ) : (
            <Languages className="mr-1 h-2 w-2" />
          )}
          {showTranscriptionTranslation && isTranscriptionTranslated
            ? t("chats.seeOriginal")
            : t("chats.translate")}
        </Button>
      </div>
      {showTranscriptionTranslation && isTranscriptionTranslated
        ? translatedTranscription
        : transcription}
    </div>
  );
};

const MessageActions = ({
  message,
  isTranslating,
  handleTranslateClick,
  showTranslation,
  isTranslatedToPreferredLanguage,
  t,
  hasAudio,
  isTranscribed,
  transcribeMessage,
  isTranscribing,
  isRead,
  canPinMessage,
  isPinned,
  handlePin,
}: {
  message: any;
  isTranslating: boolean;
  handleTranslateClick: () => void;
  showTranslation: boolean;
  isTranslatedToPreferredLanguage: boolean;
  t: any;
  hasAudio: boolean;
  isTranscribed: boolean;
  transcribeMessage: (id: string) => void;
  isTranscribing: boolean;
  isRead: boolean;
  canPinMessage: boolean;
  isPinned: boolean;
  handlePin: (event: React.BaseSyntheticEvent) => void;
}) => {
  return (
    <div className="mt-1.5 flex items-center gap-2">
      {message.text && (
        <Button
          variant="ghost"
          className="border-royal-glow-3-light text-royal-glow-3-light hover:bg-royal-glow-5 hover:text-royal-glow-3-light h-6 gap-1 rounded-lg border px-2 text-xs font-normal"
          onClick={handleTranslateClick}
          disabled={isTranslating}
        >
          {isTranslating ? (
            <RefreshCcw className="size-3 animate-spin" />
          ) : (
            <Languages className="size-3" />
          )}
          {showTranslation && isTranslatedToPreferredLanguage
            ? t("chats.seeOriginal")
            : t("chats.translate")}
        </Button>
      )}

      {hasAudio && !isTranscribed && (
        <Button
          variant="ghost"
          className="h-6 gap-1 px-2 text-xs font-normal text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          onClick={() => transcribeMessage(message.id)}
          disabled={isTranscribing}
        >
          {isTranscribing ? (
            <RefreshCcw className="size-3 animate-spin" />
          ) : (
            <AudioLines className="size-3" />
          )}
          {t("chats.transcribe")}
        </Button>
      )}

      <div className="text-xs text-neutral-400">
        <MessageTimestamp />
      </div>

      {isRead && (
        <CheckCheck className="size-3.5 text-blue-400" aria-label="Read" />
      )}

      {canPinMessage && (
        <Button
          variant="ghost"
          className="h-6 gap-1 px-2 text-xs font-normal text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600"
          onClick={handlePin}
        >
          {isPinned ? (
            <PinOff className="size-3" />
          ) : (
            <Pin className="size-3" />
          )}
          {isPinned ? t("chats.unpin") : t("chats.pin")}
        </Button>
      )}
    </div>
  );
};

const SuggestedCannedResponseCard = ({
  suggestion,
  t,
  onUse,
}: {
  suggestion: SuggestedCannedResponse;
  t: any;
  onUse: () => void;
}) => {
  const matchPercent =
    typeof suggestion.matchScore === "number"
      ? Math.round(suggestion.matchScore * 100)
      : null;

  return (
    <div className="mt-2 max-w-[420px] rounded-2xl border border-violet-200 bg-violet-50/80 p-3 text-sm shadow-sm">
      <div className="mb-2 flex items-center gap-2 text-xs font-semibold text-violet-700">
        <MessageSquareQuote className="h-4 w-4" />
        <span>
          {t("chats.cannedSuggestion.title", {
            defaultValue: "Suggested canned response",
          })}
        </span>
        {matchPercent !== null ? (
          <span className="ml-auto rounded-full bg-white px-2 py-0.5 text-[10px] text-violet-600">
            {matchPercent}%
          </span>
        ) : null}
      </div>
      <p className="line-clamp-3 whitespace-pre-wrap text-slate-700">
        {suggestion.text}
      </p>
      <div className="mt-3 flex justify-end">
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 rounded-lg border-violet-200 bg-white text-xs text-violet-700 hover:bg-violet-50"
          onClick={onUse}
        >
          {t("chats.cannedSuggestion.use", {
            defaultValue: "Use response",
          })}
        </Button>
      </div>
    </div>
  );
};

function readSuggestedCannedResponse(
  value: unknown,
): SuggestedCannedResponse | null {
  if (!value || typeof value !== "object") {
    return null;
  }

  const candidate = value as Partial<SuggestedCannedResponse>;
  if (
    typeof candidate.id !== "string" ||
    typeof candidate.text !== "string" ||
    !candidate.text.trim()
  ) {
    return null;
  }

  return {
    id: candidate.id,
    text: candidate.text,
    categoryId:
      typeof candidate.categoryId === "string"
        ? candidate.categoryId
        : undefined,
    matchScore:
      typeof candidate.matchScore === "number"
        ? candidate.matchScore
        : undefined,
    status: typeof candidate.status === "string" ? candidate.status : undefined,
  };
}

const renderMarkdownInline = (text: string) => (
  <ReactMarkdown
    remarkPlugins={[remarkGfm]}
    components={{
      p: ({ children }) => <>{children}</>,
    }}
  >
    {text.replace(/\n/g, "  \n")}
  </ReactMarkdown>
);

const renderMarkdownWithMentions = (
  text: string,
  highlightAllMention: boolean,
) => {
  if (!highlightAllMention) return renderMarkdownInline(text);

  return text.split(/(@all\b)/gi).map((part, index) => {
    if (/^@all\b$/i.test(part)) {
      return (
        <span
          key={`${part}-${index}`}
          className="bg-primary/10 text-primary rounded px-1 font-semibold"
        >
          {part}
        </span>
      );
    }

    return (
      <React.Fragment key={`${part}-${index}`}>
        {renderMarkdownInline(part)}
      </React.Fragment>
    );
  });
};

const renderFormattedMessageText = (
  text: string | undefined,
  highlightAllMention: boolean,
) => {
  if (!text) return null;

  const parts = text.split(/(\+\+[\s\S]+?\+\+)/g);

  return (
    <>
      {parts.map((part, index) => {
        const isUnderlinePart = part.startsWith("++") && part.endsWith("++");

        if (!isUnderlinePart) {
          return (
            <React.Fragment key={index}>
              {renderMarkdownWithMentions(part, highlightAllMention)}
            </React.Fragment>
          );
        }

        const underlinedText = part.slice(2, -2);

        return (
          <span key={index} className="underline decoration-solid">
            {renderMarkdownWithMentions(underlinedText, highlightAllMention)}
          </span>
        );
      })}
    </>
  );
};

function shouldRenderCannedResponseSuggestion(
  isGuestMessage: boolean,
  suggestion: SuggestedCannedResponse | null,
): boolean {
  return isGuestMessage && suggestion !== null;
}

function isGroupMessageChannel(
  chatType: unknown,
  memberCount: unknown,
  name: unknown,
): boolean {
  if (chatType === "group") {
    return true;
  }

  return (
    !chatType &&
    ((typeof memberCount === "number" && memberCount > 2) ||
      typeof name === "string")
  );
}

function shouldAutoTranslateMessage({
  isInternalChannel,
  isOwnMessage,
  messageText,
  shouldShowTranslationByDefault,
  isTranslatedToPreferredLanguage,
}: {
  isInternalChannel: boolean;
  isOwnMessage: boolean;
  messageText?: string;
  shouldShowTranslationByDefault: boolean;
  isTranslatedToPreferredLanguage: boolean;
}): boolean {
  return (
    isInternalChannel &&
    !isOwnMessage &&
    Boolean(messageText?.trim()) &&
    shouldShowTranslationByDefault &&
    !isTranslatedToPreferredLanguage
  );
}

function getMessageUiClassNames(isOwnMessage: boolean): string {
  return [
    "custom-message-ui",
    "flex",
    "flex-col",
    "mb-4",
    isOwnMessage ? "items-end" : "items-start",
  ].join(" ");
}

export const CustomMessageUi = () => {
  const { t } = useTranslation();
  const { hotel, hotelId } = useHotel();
  const {
    isMyMessage,
    message,
    readBy = [],
    handlePin,
    getMessageActions,
    highlighted,
  } = useMessageContext();
  const { channel, members = {} } = useChannelStateContext();
  const canPinMessage = getMessageActions().includes("pin");
  const isPinned = !!message.pinned;
  const { client } = useChatContext();
  const isOwnMessage = isMyMessage();
  const chatType = channel.data?.chat_type;
  const isInternalChannel = chatType === "private" || chatType === "group";

  const { mutate: translateMessage, isPending: isTranslating } =
    useTranslateMessage();
  const { mutate: transcribeMessage, isPending: isTranscribing } =
    useTranscribeMessage();
  const { preferences } = useTranslationPreferences();

  const { userProfile: user } = useAuthenticatedUser();
  const translationPreferredLanguage =
    user?.profile?.preferredLanguage || user?.profile?.language || "en";
  const isEnglishTranslation = translationPreferredLanguage
    .toLowerCase()
    .startsWith("en");
  const isGuestMessage = !isOwnMessage && !isInternalChannel;
  const shouldShowTranslationByDefault =
    Boolean(hotel?.chatSettings?.isTranslationEnabled) &&
    !(preferences.neverTranslateEnglish && isEnglishTranslation) &&
    !(preferences.showGuestOriginalMessages && isGuestMessage);

  const [showTranslation, setShowTranslation] = useState(
    shouldShowTranslationByDefault,
  );
  const [showTranscriptionTranslation, setShowTranscriptionTranslation] =
    useState(false);

  const translations = (message.translations as Record<string, string>) || {};
  const isTranslatedToPreferredLanguage =
    !!translations[translationPreferredLanguage];
  const isTranscribed = !!message.transcription;

  const translatedText = translations[translationPreferredLanguage];
  const hasAudio = message.attachments?.some(
    (a) => a.type === "voiceRecording" || a.type === "audio",
  );
  const transcription = message.transcription as string | undefined;
  const transcriptionTranslations =
    (message.transcriptionTranslations as Record<string, string>) || {};
  const isTranscriptionTranslated =
    !!transcriptionTranslations[translationPreferredLanguage];
  const translatedTranscription =
    transcriptionTranslations[translationPreferredLanguage];
  const isMessageRead =
    isOwnMessage &&
    readBy.some((user) => user.id !== (client.user?.id || client.userID));

  const externalDeliveryStatus = message.externalDeliveryStatus as
    | MessageDeliveryStatus
    | undefined;
  const externalErrorCode = message.externalErrorCode as string | undefined;
  const hasDeliveryFailed = isFailedDeliveryStatus(externalDeliveryStatus);
  const suggestedCannedResponse = readSuggestedCannedResponse(
    message.suggestedCannedResponse,
  );
  const shouldShowCannedResponseSuggestion =
    shouldRenderCannedResponseSuggestion(
      isGuestMessage,
      suggestedCannedResponse,
    );
  const isGroup = isGroupMessageChannel(
    chatType,
    channel.data?.member_count,
    channel.data?.name,
  );
  const messageAuthorId = message.user?.id;
  const isMessageAuthorOnline = Boolean(
    isGroup && messageAuthorId && members[messageAuthorId]?.user?.online,
  );
  const autoTranslationRequestRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const requestKey = `${message.id}:${translationPreferredLanguage}`;
    const shouldAutoTranslate = shouldAutoTranslateMessage({
      isInternalChannel,
      isOwnMessage,
      messageText: message.text,
      shouldShowTranslationByDefault,
      isTranslatedToPreferredLanguage,
    });

    if (
      !shouldAutoTranslate ||
      autoTranslationRequestRef.current === requestKey
    ) {
      return;
    }

    autoTranslationRequestRef.current = requestKey;
    translateMessage(
      {
        messageId: message.id,
        targetLanguage: translationPreferredLanguage,
      },
      {
        onSuccess: () => setShowTranslation(true),
      },
    );
  }, [
    isInternalChannel,
    isOwnMessage,
    isTranslatedToPreferredLanguage,
    message.id,
    message.text,
    shouldShowTranslationByDefault,
    translateMessage,
    translationPreferredLanguage,
  ]);

  React.useEffect(() => {
    if (isTranscriptionTranslated) {
      setShowTranscriptionTranslation(true);
    }
  }, [isTranscriptionTranslated]);

  React.useEffect(() => {
    setShowTranslation(shouldShowTranslationByDefault);
  }, [shouldShowTranslationByDefault]);

  const messageUiClassNames = getMessageUiClassNames(isOwnMessage);

  const displayMessage = {
    ...message,
    text:
      showTranslation && isTranslatedToPreferredLanguage
        ? translatedText
        : message.text,
  };

  const handleTranslateClick = () => {
    if (isTranslatedToPreferredLanguage) {
      setShowTranslation(!showTranslation);
    } else {
      translateMessage(
        { messageId: message.id, targetLanguage: translationPreferredLanguage },
        { onSuccess: () => setShowTranslation(true) },
      );
    }
  };

  const handleTranslateTranscriptionClick = () => {
    if (isTranscriptionTranslated) {
      setShowTranscriptionTranslation(!showTranscriptionTranslation);
    } else {
      translateMessage(
        {
          messageId: message.id,
          targetLanguage: translationPreferredLanguage,
          translateTranscription: true,
        },
        { onSuccess: () => setShowTranscriptionTranslation(true) },
      );
    }
  };

  const bubbleBackground = getBubbleBackground(
    isOwnMessage,
    Boolean(highlighted),
  );
  const bubbleStyles = hasDeliveryFailed
    ? `${bubbleBackground} ring-1 ring-destructive/40`
    : bubbleBackground;

  const highlightAllMention = isAllMentionMessage(message);

  const handleUseSuggestedCannedResponse = () => {
    if (!suggestedCannedResponse) return;

    dispatchChatComposerInsertText({
      text: suggestedCannedResponse.text,
      mode: "replace",
    });
  };

  return (
    <div className={messageUiClassNames} data-message-id={message.id}>
      <div
        className={`flex w-full max-w-[85%] items-start gap-3 ${isOwnMessage ? "flex-row-reverse" : "flex-row"}`}
      >
        <UserAvatar
          isMyMessage={isOwnMessage}
          isOnline={isMessageAuthorOnline}
          message={message}
          onlineLabel={t("chats.online")}
        />

        <div
          className={`flex min-w-0 flex-col ${isOwnMessage ? "items-end" : "items-start"}`}
        >
          <div
            className={`flex w-fit max-w-full flex-col gap-2 overflow-hidden rounded-lg p-3 break-words whitespace-pre-wrap transition-colors duration-200 ${bubbleStyles}`}
          >
            <div className="text-sm break-words whitespace-pre-wrap text-neutral-950">
              {renderFormattedMessageText(
                displayMessage.text,
                highlightAllMention,
              )}
            </div>
            {message.requestId && (
              <RequestMessageDisplay requestId={message.requestId as string} />
            )}

            <TranscriptionSection
              isTranscribed={isTranscribed}
              t={t}
              handleTranslateTranscriptionClick={
                handleTranslateTranscriptionClick
              }
              isTranslating={isTranslating}
              showTranscriptionTranslation={showTranscriptionTranslation}
              isTranscriptionTranslated={isTranscriptionTranslated}
              translatedTranscription={translatedTranscription}
              transcription={transcription}
            />
            {message.attachments && message.attachments.length > 0 && (
              <Attachment attachments={message.attachments} />
            )}
            <WhatsAppMediaAttachment
              hotelId={hotelId}
              externalChannel={message.externalChannel as string | undefined}
              mediaKind={
                message.externalMediaKind as "image" | "document" | undefined
              }
              mediaId={message.externalMediaId as string | undefined}
              mimeType={message.externalMediaMimeType as string | undefined}
              caption={message.externalMediaCaption as string | undefined}
              filename={message.externalMediaFilename as string | undefined}
              whatsappMessageId={
                message.externalWhatsAppMessageId as string | undefined
              }
              whatsappConversationId={
                message.externalWhatsAppConversationId as string | undefined
              }
            />
          </div>

          <MessageActions
            message={message}
            isTranslating={isTranslating}
            handleTranslateClick={handleTranslateClick}
            showTranslation={showTranslation}
            isTranslatedToPreferredLanguage={isTranslatedToPreferredLanguage}
            t={t}
            hasAudio={!!hasAudio}
            isTranscribed={isTranscribed}
            transcribeMessage={transcribeMessage}
            isTranscribing={isTranscribing}
            isRead={isMessageRead}
            canPinMessage={canPinMessage}
            isPinned={isPinned}
            handlePin={handlePin}
          />
          <ExternalChannelBadge
            external={message.external as boolean | undefined}
            channel={
              message.externalChannel as ResolvedChannelValue | undefined
            }
          />
          <MessageDeliveryFailure
            status={externalDeliveryStatus}
            errorCode={externalErrorCode}
            channel={
              message.externalChannel as ResolvedChannelValue | undefined
            }
          />
          {shouldShowCannedResponseSuggestion && suggestedCannedResponse ? (
            <SuggestedCannedResponseCard
              suggestion={suggestedCannedResponse}
              t={t}
              onUse={handleUseSuggestedCannedResponse}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};
